/**
 * FINCRA 走廊（payout corridor）界面辅助工具：金丝雀「银行账户绑定」页与「API 文档」页共用。
 *
 * 为什么需要：Fincra 出款时 beneficiary.mobileMoneyCode / beneficiary.bankCode 必须是它 List Banks 接口返回的 code，
 * 而且 code 按国家区分（sandbox 实测 KE 的 M-Pesa 是 SAFARICOM，传 MPESA 会被拒）。所以绑卡前要先按
 * 「收款国家 + 币种」查走廊接口（GET /v1/fiat/payment/channel/config/payout/requirements，由 test-platform 代理），
 * 让测试同学从列表里选机构，再把 code 填进绑卡 extParam。
 *
 * 这里只放「界面辅助」，不做任何合法性判断：
 *   - 币种 → 收款国家的默认值：预填绑卡页 bankCountry、给走廊查询带 country；用户仍可手改（用来构造反例）。
 *   - 从走廊响应里取某个出款目的地（paymentDestination）的机构列表。
 *   - 选中机构后要回填到表单的字段。
 * 「国家与币种是否对应」「某币种开放哪些出款目的地」「code 是否有效」都由 fiat-payment 判定：
 * 走廊接口对不支持的组合返回 INVALID_PARAMETER（面板会把 msg 展示出来），绑卡字段规则在字段注册中心配置。
 *
 * 响应结构依据：fiat-payment PayoutRequirementsResponse（payoutMethods[].payoutType / requiredFields / providers[]），
 * 见 fiat-payment docs/fincra-payout-withdraw-2026-10.md 第 3.1 节。
 */

export const FINCRA_CHANNEL = 'FINCRA';

/**
 * 出款目的地取值（Fincra 协议值），也是走廊响应 payoutMethods[].payoutType 与绑卡 extParam.paymentDestination 的取值。
 */
export const FINCRA_PAYMENT_DESTINATIONS = Object.freeze({
  MOBILE_MONEY_WALLET: 'mobile_money_wallet',
  BANK_ACCOUNT: 'bank_account',
});

/**
 * 本期走廊：币种 → 收款国家（ISO 3166-1 alpha-2）。
 *
 * 依据 fiat-payment FincraChannelConstants.PAYOUT_COUNTRY_BY_CURRENCY：本期每个币种只对应一个国家，XOF 只做科特迪瓦（CI）。
 * 后端改了走廊这里要同步；没同步时前端只是预填了一个会被拒的国家，走廊查询会返回后端的报错信息（含支持列表），不会静默出错。
 * 用 Object.freeze 防止调用方意外改写共享常量。
 */
export const FINCRA_COUNTRY_BY_CURRENCY = Object.freeze({
  UGX: 'UG',
  GHS: 'GH',
  KES: 'KE',
  TZS: 'TZ',
  ZMW: 'ZM',
  XOF: 'CI',
});

/**
 * 币种对应的默认收款国家。
 *
 * @param {string} currency 币种，大小写不敏感，允许前后空格
 * @returns {string|undefined} 国家码；不在本期走廊里（或入参为空 / 非字符串）时返回 undefined，调用方据此「不预填」
 */
export const fincraCountryOf = (currency) => {
  if (typeof currency !== 'string') return undefined;
  return FINCRA_COUNTRY_BY_CURRENCY[currency.trim().toUpperCase()];
};

/**
 * 某收款国家在本期走廊里对应的币种（反查 FINCRA_COUNTRY_BY_CURRENCY；本期一国一币，返回数组是为了与通用的
 * 「国家 → 币种列表」接口形状一致）。
 *
 * @param {string} country 国家码，大小写不敏感
 * @returns {string[]} 不在走廊里时返回空数组
 */
export const fincraCurrenciesOfCountry = (country) => {
  if (typeof country !== 'string') return [];
  const target = country.trim().toUpperCase();
  return Object.entries(FINCRA_COUNTRY_BY_CURRENCY).filter(([, c]) => c === target).map(([currency]) => currency);
};

/**
 * 国家码 → 国旗 emoji（两个区域指示符号拼接），纯展示用，免得再手写一份国旗表。
 * 不是两位英文字母时返回空串。
 */
export const countryFlag = (country) => {
  if (typeof country !== 'string' || !/^[A-Za-z]{2}$/.test(country.trim())) return '';
  return String.fromCodePoint(...country.trim().toUpperCase().split('').map(ch => 0x1F1E6 + ch.charCodeAt(0) - 65));
};

/**
 * API 文档页走廊面板的国家下拉：只列本期走廊的 6 个国家（其它国家查了也只会被后端拒绝）。
 * 顺序跟随 FINCRA_COUNTRY_BY_CURRENCY。
 */
export const fincraCorridorCountryOptions = () =>
  Object.values(FINCRA_COUNTRY_BY_CURRENCY).map(country => ({ value: country, label: `${countryFlag(country)} ${country}` }));

/**
 * 走廊查询需要的两个参数是否齐全：国家 2 位、币种 3 位（与 fiat-payment 接口的入参格式一致）。
 * 只判断格式是否「值得发请求」，不判断组合是否被支持（那是后端的事）。
 */
export const canQueryCorridor = (country, currency) =>
  typeof country === 'string' && country.trim().length === 2
  && typeof currency === 'string' && currency.trim().length === 3;

/**
 * 解析 test-platform 透传的走廊接口响应。
 *
 * test-platform 原样返回 fiat-payment 的 ResponseResult：成功为 {code: 200 | "200", data: {payoutMethods: [...]}}；
 * 失败（走廊不支持、商户没配渠道账户等）为 {code: 非 200, msg}；test-platform 自己调用异常时为 {code: "500", msg: "查询失败: ..."}。
 * V1 接口在 Fincra 查询失败时返回 code=200 + data=null，所以「成功但没有 payoutMethods」也要单独识别为空结果。
 *
 * @param {object} body axios 响应的 data
 * @returns {{ok: boolean, data: object|null, message: string|null}}
 *          ok=true 时 data 一定带非空 payoutMethods；ok=false 时 message 为后端报错（可能为 null，表示「空结果」）
 */
export const parseCorridorResponse = (body) => {
  const success = body != null && String(body.code) === '200';
  const data = success ? body.data : null;
  if (data && Array.isArray(data.payoutMethods) && data.payoutMethods.length > 0) {
    return { ok: true, data, message: null };
  }
  return { ok: false, data: null, message: success ? null : (body?.msg || null) };
};

/** 走廊响应里开放的出款目的地列表（保持后端顺序：Mobile Money 在前） */
export const payoutTypesOf = (requirements) =>
  (requirements?.payoutMethods || []).map(m => m?.payoutType).filter(Boolean);

/**
 * 某出款目的地下可选的机构。
 *
 * @param {object} requirements parseCorridorResponse 返回的 data
 * @param {string} destination  出款目的地（mobile_money_wallet / bank_account）
 * @returns {Array<{code: string, name?: string, type?: string, swiftCode?: string, bic?: string}>}
 *          该目的地未开放（如 UGX 的 bank_account）或没有机构时返回空数组；过滤掉没有 code 的条目（code 是回填的唯一依据）
 */
export const providersOf = (requirements, destination) => {
  const method = (requirements?.payoutMethods || []).find(m => m?.payoutType === destination);
  return (method?.providers || []).filter(p => p && typeof p.code === 'string' && p.code.trim() !== '');
};

/**
 * 机构下拉的选项。
 *
 * value 用「下标」而不是 code：Fincra 的 code 不唯一（KE 有多家银行 code 都是 35），用 code 当 value 会让
 * 不同银行选中同一项、回填错银行名 / BIC。label 带上名称与 code，测试同学能看出选的是谁。
 */
export const providerOptions = (providers) =>
  (providers || []).map((p, index) => ({
    value: index,
    label: p.name ? `${p.name} · ${p.code}` : p.code,
  }));

/**
 * 选中机构后要回填到绑卡表单的字段（绑卡页 extParam 字段的表单名带 extParam_ 前缀）。
 *
 * - Mobile Money：code → extParam_mobileMoneyCode；运营商没有 BIC，bic 清空，避免沿用上一次选的银行的 BIC。
 * - 银行：code → extParam_bankCode；bic 取 provider.bic，没有再取 swiftCode，都没有就清空。
 * - bankName 一律取机构名称（与渠道文档示例一致：Mobile Money 卡的 bankName 填运营商名，如 Safaricom(MPESA)），
 *   bankName / bic 在 FINCRA 只做留档、不下发，填上便于在卡列表里辨认。
 * 不去清另一个 code 字段：切换出款目的地后两边都可能有值，是否允许由配置判定，保留给反例测试。
 *
 * @param {object} provider    providersOf 返回的某一项；为空时返回空对象（不改表单）
 * @param {string} destination 当前出款目的地
 * @returns {object} 可直接传给 form.setFieldsValue 的补丁
 */
export const providerFormPatch = (provider, destination) => {
  if (!provider || typeof provider.code !== 'string' || provider.code.trim() === '') return {};
  const code = provider.code.trim();
  const patch = { bankName: provider.name || undefined };
  if (destination === FINCRA_PAYMENT_DESTINATIONS.MOBILE_MONEY_WALLET) {
    patch.extParam_mobileMoneyCode = code;
    patch.bic = undefined;
  } else {
    patch.extParam_bankCode = code;
    patch.bic = provider.bic || provider.swiftCode || undefined;
  }
  return patch;
};
